import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import { isOnRequestBooking } from "@ckgo2/shared/utils";
import { ArrowLeft, Check } from "lucide-react";
import { TripStep, CustomerStep, DetailsStep, ReviewStep } from "@/components/orders/new-order";
import type {
  Trip,
  TripDate,
  Customer,
  Participant,
  NewCustomerForm,
  Step,
} from "@/components/orders/new-order";

const STEPS: { id: Step; label: string }[] = [
  { id: "trip", label: "Trip" },
  { id: "customer", label: "Customer" },
  { id: "details", label: "Details" },
  { id: "review", label: "Summary" },
];

const STEP_IDS = STEPS.map((s) => s.id);

// Minimal lead shape needed to prefill the wizard when converting (?leadId=).
interface LeadPrefill {
  id: string;
  tripId?: string | null;
  customerId?: string | null;
  // Contact fields — used to pre-select/pre-fill the customer when the lead has no customerId
  // yet (a web inquiry resolves one, but a manually-created lead often won't).
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  paxAdults: number;
  paxChildren: number;
  requestedDate?: string | null;
  customerNotes?: string | null;
  internalNotes?: string | null;
}

export default function NewOrderPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const leadId = searchParams.get("leadId");

  // Form state
  const [step, setStep] = useState<Step>("trip");
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [selectedDate, setSelectedDate] = useState<TripDate | null>(null);
  const [requestedDate, setRequestedDate] = useState<string>("");
  const [customerMode, setCustomerMode] = useState<"search" | "new">("search");
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [tripSearch, setTripSearch] = useState("");

  // New customer form
  const [newCustomer, setNewCustomer] = useState<NewCustomerForm>({
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    street: "",
    city: "",
    zip: "",
  });

  // Order details
  const [adultsCount, setAdultsCount] = useState(1);
  const [childrenCount, setChildrenCount] = useState(0);
  const [participants, setParticipants] = useState<Participant[]>([{ name: "" }]);
  const [customerNotes, setCustomerNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [status, setStatus] = useState("confirmed");

  // Fetch trips
  const { data: tripsData, isLoading: tripsLoading } = useQuery({
    queryKey: ["trips-for-order"],
    queryFn: () => api.get<ApiResponse<Trip[]>>("/api/admin/trips"),
  });

  // Fetch selected trip details (includes dates)
  const { data: tripDetailsData, isLoading: tripDetailsLoading } = useQuery({
    queryKey: ["trip-details", selectedTrip?.id],
    queryFn: () =>
      api.get<ApiResponse<Trip & { dates: TripDate[] }>>(`/api/admin/trips/${selectedTrip?.id}`),
    enabled: !!selectedTrip?.id,
  });

  // Fetch customers for the picker (the list is now paginated — pull a generous page).
  const { data: customersData, isLoading: customersLoading } = useQuery({
    queryKey: ["customers-for-order"],
    queryFn: () => api.get<ApiResponse<Customer[]>>("/api/admin/customers?limit=100"),
  });

  // Lead being converted (optional). Prefills trip/customer/pax/notes once lists load.
  const { data: leadData } = useQuery({
    queryKey: ["lead-for-order", leadId],
    queryFn: () => api.get<ApiResponse<LeadPrefill>>(`/api/admin/leads/${leadId}`),
    enabled: !!leadId,
  });

  const trips = tripsData?.data || [];
  const customers = customersData?.data || [];
  const tripDates = tripDetailsData?.data?.dates || [];

  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    const lead = leadData?.data;
    if (!lead || prefilled) return;
    if (lead.tripId) {
      const t = trips.find((x) => x.id === lead.tripId);
      if (t) setSelectedTrip(t);
    }
    // Pre-select the customer: by the lead's customerId, else match an existing one by the
    // contact email, else drop into "new customer" mode pre-filled from the lead's contact —
    // so converting a lead never makes the agent re-type who the customer is.
    const byId = lead.customerId ? customers.find((x) => x.id === lead.customerId) : undefined;
    const byEmail =
      !byId && lead.contactEmail
        ? customers.find((x) => x.email.toLowerCase() === lead.contactEmail!.toLowerCase())
        : undefined;
    const matched = byId ?? byEmail;
    if (matched) {
      setSelectedCustomer(matched);
    } else if (lead.contactEmail || lead.contactName) {
      const [firstName, ...rest] = (lead.contactName ?? "").trim().split(/\s+/);
      setCustomerMode("new");
      setNewCustomer((prev) => ({
        ...prev,
        email: lead.contactEmail ?? "",
        firstName: firstName ?? "",
        lastName: rest.join(" "),
        phone: lead.contactPhone ?? "",
      }));
    }
    setAdultsCount(lead.paxAdults || 1);
    setChildrenCount(lead.paxChildren || 0);
    if (lead.requestedDate) setRequestedDate(lead.requestedDate);
    if (lead.customerNotes) setCustomerNotes(lead.customerNotes);
    if (lead.internalNotes) setInternalNotes(lead.internalNotes);
    setStatus("inquiry"); // converted leads start as a fresh inquiry-stage order
    setPrefilled(true);
  }, [leadData, trips, customers, prefilled]);

  // Filter trips
  const filteredTrips = trips.filter(
    (t) =>
      t.title.toLowerCase().includes(tripSearch.toLowerCase()) ||
      t.slug.toLowerCase().includes(tripSearch.toLowerCase()),
  );

  // Filter customers
  const filteredCustomers = customers.filter(
    (c) =>
      c.email.toLowerCase().includes(customerSearch.toLowerCase()) ||
      c.firstName?.toLowerCase().includes(customerSearch.toLowerCase()) ||
      c.lastName?.toLowerCase().includes(customerSearch.toLowerCase()) ||
      c.phone?.includes(customerSearch),
  );

  // Update participants array when adults count changes
  useEffect(() => {
    const totalCount = adultsCount + childrenCount;
    setParticipants((prev) => {
      if (prev.length < totalCount) {
        return [...prev, ...Array(totalCount - prev.length).fill({ name: "" })];
      } else if (prev.length > totalCount) {
        return prev.slice(0, totalCount);
      }
      return prev;
    });
  }, [adultsCount, childrenCount]);

  // Calculate total price (on-request uses trip.priceFrom; fixed-departures uses tripDate.price)
  const isOnRequest = isOnRequestBooking(selectedTrip ?? {});
  const pricePerPerson = isOnRequest
    ? selectedTrip?.priceFrom || 0
    : selectedDate
      ? selectedDate.price
      : 0;
  const totalPrice = pricePerPerson * (adultsCount + childrenCount * 0.5);
  const depositAmount = Math.round(totalPrice * 0.5);

  // Create customer mutation
  const createCustomerMutation = useMutation({
    mutationFn: (data: NewCustomerForm) =>
      api.post<ApiResponse<Customer>>("/api/admin/customers", data),
    onSuccess: (data) => {
      setSelectedCustomer(data.data);
      setCustomerMode("search");
      queryClient.invalidateQueries({ queryKey: ["customers-for-order"] });
    },
  });

  // Create order mutation. When a leadId is passed, the backend createOrder pipeline links the
  // lead back (convertedOrderId + sold) idempotently — no separate PUT needed here anymore.
  const createOrderMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<ApiResponse<{ id: string }>>("/api/admin/orders", data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      if (leadId) queryClient.removeQueries({ queryKey: ["leads"] });
      navigate(`/orders/${data.data.id}`);
    },
  });

  const handleCreateCustomer = async () => {
    if (!newCustomer.email || !newCustomer.firstName || !newCustomer.lastName) {
      return;
    }
    createCustomerMutation.mutate(newCustomer);
  };

  const handleSubmit = async () => {
    if (!selectedTrip || !selectedCustomer) return;
    if (isOnRequest ? !requestedDate : !selectedDate) return;

    const orderData = {
      customerId: selectedCustomer.id,
      tripId: selectedTrip.id,
      ...(leadId ? { leadId } : {}),
      ...(isOnRequest ? { requestedDate } : { tripDateId: selectedDate!.id }),
      status,
      participants: participants.filter((p) => p.name),
      adultsCount,
      childrenCount,
      totalPrice: Math.round(totalPrice),
      customerNotes,
      internalNotes,
      source: "phone",
    };

    createOrderMutation.mutate(orderData);
  };

  const canProceedToCustomer =
    !!selectedTrip && (isOnRequest ? !!requestedDate : !!selectedDate) && !tripDetailsLoading;
  const canProceedToDetails = !!selectedCustomer;
  const canProceedToReview = participants.some((p) => p.name);

  return (
    <div className="mx-auto max-w-4xl">
      {/* Header */}
      <div className="mb-6 flex items-center gap-4">
        <button onClick={() => navigate("/orders")} className="btn-ghost btn-sm">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">New order</h1>
          <p className="text-sm text-gray-500">Manual order creation</p>
        </div>
      </div>

      {/* Progress steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {STEPS.map((s, index) => (
            <div key={s.id} className="flex items-center">
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                  step === s.id
                    ? "bg-primary-600 text-white"
                    : index < STEP_IDS.indexOf(step)
                      ? "bg-success-500 text-white"
                      : "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
                }`}
              >
                {index < STEP_IDS.indexOf(step) ? <Check className="h-4 w-4" /> : index + 1}
              </div>
              <span
                className={`ml-2 hidden text-sm sm:block ${
                  step === s.id ? "font-medium text-gray-900 dark:text-white" : "text-gray-500"
                }`}
              >
                {s.label}
              </span>
              {index < 3 && <div className="mx-4 h-0.5 w-8 bg-gray-200 sm:w-16 dark:bg-gray-700" />}
            </div>
          ))}
        </div>
      </div>

      {/* Step content */}
      <div className="card">
        {step === "trip" && (
          <TripStep
            tripSearch={tripSearch}
            setTripSearch={setTripSearch}
            tripsLoading={tripsLoading}
            filteredTrips={filteredTrips}
            selectedTrip={selectedTrip}
            setSelectedTrip={setSelectedTrip}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            requestedDate={requestedDate}
            setRequestedDate={setRequestedDate}
            tripDetailsLoading={tripDetailsLoading}
            tripDates={tripDates}
            canProceedToCustomer={canProceedToCustomer}
            onNext={() => setStep("customer")}
          />
        )}

        {step === "customer" && (
          <CustomerStep
            customerMode={customerMode}
            setCustomerMode={setCustomerMode}
            customerSearch={customerSearch}
            setCustomerSearch={setCustomerSearch}
            customersLoading={customersLoading}
            filteredCustomers={filteredCustomers}
            selectedCustomer={selectedCustomer}
            setSelectedCustomer={setSelectedCustomer}
            newCustomer={newCustomer}
            setNewCustomer={setNewCustomer}
            handleCreateCustomer={handleCreateCustomer}
            createCustomerPending={createCustomerMutation.isPending}
            canProceedToDetails={canProceedToDetails}
            onBack={() => setStep("trip")}
            onNext={() => setStep("details")}
          />
        )}

        {step === "details" && (
          <DetailsStep
            adultsCount={adultsCount}
            setAdultsCount={setAdultsCount}
            childrenCount={childrenCount}
            setChildrenCount={setChildrenCount}
            participants={participants}
            setParticipants={setParticipants}
            status={status}
            setStatus={setStatus}
            customerNotes={customerNotes}
            setCustomerNotes={setCustomerNotes}
            internalNotes={internalNotes}
            setInternalNotes={setInternalNotes}
            canProceedToReview={canProceedToReview}
            onBack={() => setStep("customer")}
            onNext={() => setStep("review")}
          />
        )}

        {step === "review" && (
          <ReviewStep
            selectedTrip={selectedTrip}
            selectedDate={selectedDate}
            requestedDate={requestedDate}
            selectedCustomer={selectedCustomer}
            adultsCount={adultsCount}
            childrenCount={childrenCount}
            participants={participants}
            pricePerPerson={pricePerPerson}
            totalPrice={totalPrice}
            depositAmount={depositAmount}
            customerNotes={customerNotes}
            internalNotes={internalNotes}
            handleSubmit={handleSubmit}
            createOrderPending={createOrderMutation.isPending}
            createOrderError={createOrderMutation.isError}
            onBack={() => setStep("details")}
          />
        )}
      </div>
    </div>
  );
}
